import React, { useState, useMemo, useRef } from 'react';
import { FaFutbol, FaCheck, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { sportColors } from '../../utils/sports';
import { useBetsData } from '../../utils/fetchBetsData';
import { computeBetStats } from '../../utils/betStats';
import PageHeader from '../../components/PageHeader/PageHeader';
import BetsSummary from '../../components/BetsSummary/BetsSummary';
import { SkeletonBetList } from '../../components/Skeleton/Skeleton';
import '../BetsPage/BetsPage.css';

const COMPETITION = 'Mundial 2026';

const sportIcons = {
    futebol: FaFutbol,
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const [year, month, day] = dateStr.split('T')[0].split('-');
        return `${day}-${month}-${year}`;
    }
    return String(dateStr);
};

const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const parts = timeStr.split(':');
    return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : timeStr;
};

function Mundial2026Page() {
    const { betsData, loading, error } = useBetsData();

    const [currentPage, setCurrentPage] = useState(1);
    const [isPageChanging, setIsPageChanging] = useState(false);
    const betsPerPage = 10;
    const betsListRef = useRef(null);

    const mundialBets = useMemo(() =>
        betsData.filter(bet => bet.games.some(g => g.competition === COMPETITION)),
        [betsData]
    );

    const stats = useMemo(() => computeBetStats(mundialBets), [mundialBets]);

    const totalPages = Math.ceil(mundialBets.length / betsPerPage);
    const currentBets = mundialBets.slice((currentPage - 1) * betsPerPage, currentPage * betsPerPage);

    const goToPage = (page) => {
        const next = Math.max(1, Math.min(page, totalPages));
        if (next !== currentPage) {
            setIsPageChanging(true);
            setTimeout(() => {
                setCurrentPage(next);
                setIsPageChanging(false);
                betsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 150);
        }
    };

    const header = (
        <PageHeader
            eyebrow="Competição"
            title="Mundial 2026"
            subtitle="🇺🇸 USA · 🇨🇦 Canada · 🇲🇽 México — todas as apostas do torneio."
        />
    );

    if (loading || error) {
        return (
            <div className="page-bg-overlay">
                <main className="page-container bets-page-container">
                    {header}
                    {loading ? (
                        <SkeletonBetList />
                    ) : (
                        <div className="error-state"><p>Erro ao carregar apostas: {error}</p></div>
                    )}
                </main>
            </div>
        );
    }

    return (
        <div className="page-bg-overlay">
            <main className="page-container bets-page-container">

                {header}

                <BetsSummary stats={stats} bets={mundialBets} />

                <div className={`bets-list ${isPageChanging ? 'page-changing' : ''}`} ref={betsListRef}>
                    {mundialBets.length === 0 ? (
                        <div className="no-bets">
                            <p>Nenhuma aposta encontrada.</p>
                        </div>
                    ) : (
                        currentBets.map(bet => {
                            const potentialWin = ((bet.amount || 0) * bet.totalOdd).toFixed(2);
                            const betType = bet.games.length > 1 ? `Múltipla (${bet.games.length})` : 'Simples';
                            return (
                                <div key={bet.id} className={`bet-card ${bet.result}`}>
                                    <div className="bet-card-header">
                                        <div className="bet-header-left">
                                            <div className="bet-type-badge">{betType}</div>
                                            {bet.date && <span className="bet-date">{formatDate(bet.date)}</span>}
                                        </div>
                                        <div className={`bet-result-badge ${bet.result}`}>
                                            {bet.result === 'won' ? (
                                                <><FaCheck className="result-icon" /><span>Ganho</span></>
                                            ) : bet.result === 'lost' ? (
                                                <><FaTimes className="result-icon" /><span>Perdido</span></>
                                            ) : bet.result === 'void' ? (
                                                <span>Anulada</span>
                                            ) : (
                                                <span>Pendente</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bet-games-list">
                                        {bet.games.map((game, index) => {
                                            const GameIcon = sportIcons[game.sport] || FaFutbol;
                                            const gameResultClass = game.gameResult ? `game-${game.gameResult}` : '';
                                            const isBetBuilder = game.extraPicks && game.extraPicks.length > 0;
                                            const selections = isBetBuilder
                                                ? [{ pick: game.pick, odd: game.gameOdd }, ...game.extraPicks]
                                                : [];
                                            const displayOdd = isBetBuilder ? game.combinedOdd : game.gameOdd;
                                            return (
                                                <div key={game.id} className={`bet-game-item ${gameResultClass}`}>
                                                    <div className="bets-game-sport-icon" style={{ color: sportColors[game.sport] || '#ffd24d' }}>
                                                        <GameIcon />
                                                    </div>
                                                    <div className="bets-game-info">
                                                        {isBetBuilder ? (
                                                            <>
                                                                <span className="bets-game-teams">
                                                                    {game.homeTeam}{game.awayTeam ? ` vs ${game.awayTeam}` : ''}
                                                                </span>
                                                                <span className="bets-game-datetime">
                                                                    {formatDate(game.date)} · {formatTime(game.time)}
                                                                </span>
                                                                <div className="bb-sub-timeline">
                                                                    {selections.map((sel, selIndex) => (
                                                                        <div className="bb-sub-pick" key={selIndex}>
                                                                            <span className="bb-sub-dot"></span>
                                                                            <span className="bb-sub-label">{sel.pick || `Seleção ${selIndex + 1}`}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {(game.pick || game.gameResult) && (
                                                                    <div className="bets-game-pick-row">
                                                                        {game.pick && (
                                                                            <span className={`bets-game-pick-value ${gameResultClass}`}>{game.pick}</span>
                                                                        )}
                                                                        {game.gameResult === 'void' && <span className="void-badge">Anulado</span>}
                                                                        {game.gameResult && game.gameResult !== 'pending' && game.gameResult !== 'void' && (
                                                                            <div className={`game-result-indicator ${game.gameResult}`}>
                                                                                {game.gameResult === 'won' ? <FaCheck /> : <FaTimes />}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <span className="bets-game-teams">
                                                                    {game.homeTeam}{game.awayTeam ? ` vs ${game.awayTeam}` : ''}
                                                                </span>
                                                                <span className="bets-game-datetime">
                                                                    {formatDate(game.date)} · {formatTime(game.time)}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className={`bets-game-odd-display ${gameResultClass}`}>
                                                        <span className="bets-odd-label">Odd</span>
                                                        <span className="bets-odd-value">{isBetBuilder ? Number(displayOdd).toFixed(2) : game.gameOdd}</span>
                                                    </div>
                                                    {index < bet.games.length - 1 && <div className="bets-game-divider"></div>}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="bet-card-footer">
                                        <div className="bet-stat">
                                            <span className="bet-stat-label">Odd Total</span>
                                            <span className="bet-stat-value">{bet.totalOdd.toFixed(2)}</span>
                                        </div>
                                        <div className="bet-stat">
                                            <span className="bet-stat-label">Valor Apostado</span>
                                            <span className="bet-stat-value">{(bet.amount || 0).toFixed(2)}€</span>
                                        </div>
                                        <div className={`bet-stat ${bet.result === 'won' ? 'highlight-won' : bet.result === 'lost' ? 'highlight-lost' : bet.result === 'void' ? 'highlight-void' : ''}`}>
                                            <span className="bet-stat-label">
                                                {bet.result === 'won' ? 'Ganho' : bet.result === 'void' ? 'Anulada' : 'Potencial'}
                                            </span>
                                            <span className="bet-stat-value">{bet.result === 'void' ? '-' : `${potentialWin}€`}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>

                {totalPages > 1 && (
                    <div className="pagination">
                        <button className="pagination-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                            <FaChevronLeft />
                        </button>
                        <div className="pagination-info">
                            <span className="pagination-current">{currentPage}</span>
                            <span className="pagination-separator">/</span>
                            <span className="pagination-total">{totalPages}</span>
                        </div>
                        <button className="pagination-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                            <FaChevronRight />
                        </button>
                    </div>
                )}

            </main>
        </div>
    );
}

export default Mundial2026Page;
